import { useEffect, useRef, useState } from "react";
import { Bot, X, Send } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { API_BASE } from "@/lib/api";

// Static markdown renderer config — extracted so ReactMarkdown doesn't get a new
// object on every render (avoids unnecessary re-renders inside message bubbles).
const MD_PLUGINS = [remarkGfm];
const MD_COMPONENTS = {
  a: (props) => (
    <a {...props} target="_blank" rel="noreferrer" className="text-[#007BFF] underline underline-offset-2" />
  ),
};

function newSessionId() {
  let s = localStorage.getItem("gms_chat_session");
  if (!s) {
    s = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("gms_chat_session", s);
  }
  return s;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi — I'm the GMS AI Assistant. How can I help? Ask me about machines, industries, service or request a quote." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(newSessionId());
  const scrollerRef = useRef(null);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 999999, behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || busy) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: userMsg }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: userMsg }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      // Read SSE stream
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === "delta") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.content };
                return copy;
              });
            } else if (data.type === "error") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "Sorry — I couldn't respond right now. Please try again or use the Talk to an Expert form." };
                return copy;
              });
            }
          } catch (parseErr) {
            // Malformed SSE frame — log for debugging but keep reading the stream.
            console.error("ChatWidget: failed to parse SSE frame", parseErr, line);
          }
        }
      }
    } catch (err) {
      console.error("ChatWidget: stream connection failed", err);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection issue. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="chat-open-btn"
        aria-label="Open GMS AI Assistant"
        className={`fixed bottom-20 md:bottom-6 right-6 z-40 w-14 h-14 bg-[#0F3D66] hover:bg-[#0a2b48] text-white flex items-center justify-center rounded-full shadow-lg transition-colors ${open ? "hidden" : ""}`}
      >
        <Bot className="w-6 h-6" strokeWidth={2} />
      </button>

      {open && (
        <div className="fixed bottom-20 md:bottom-6 right-6 z-40 w-[92vw] max-w-[400px] h-[560px] bg-white/95 backdrop-blur border border-[#E2E8F0] shadow-2xl flex flex-col rounded-sm" data-testid="chat-widget">
          <div className="bg-[#0F3D66] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5" strokeWidth={2} />
              <div>
                <div className="font-heading font-bold text-sm">GMS AI Assistant</div>
                <div className="text-[10px] uppercase tracking-widest opacity-80">Online · Claude 4.6</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-btn" aria-label="close"><X className="w-5 h-5" /></button>
          </div>
          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F5F7FA]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-sm text-sm leading-relaxed ${m.role === "user" ? "bg-[#007BFF] text-white whitespace-pre-wrap" : "bg-white text-[#0F3D66] border border-[#E2E8F0]"}`} data-testid={`chat-msg-${m.role}`}>
                  {m.role === "assistant" ? (
                    <div className="gms-md">
                      {m.content ? (
                        <ReactMarkdown
                          remarkPlugins={MD_PLUGINS}
                          components={MD_COMPONENTS}
                        >
                          {m.content}
                        </ReactMarkdown>
                      ) : (busy && i === messages.length - 1 ? "…" : "")}
                    </div>
                  ) : (
                    m.content
                  )}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="p-3 border-t border-[#E2E8F0] bg-white flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about machines, service, quotes…"
              className="flex-1 bg-[#F5F7FA] border border-transparent focus:bg-white focus:border-[#007BFF] focus:outline-none px-3 py-2 text-sm rounded-sm"
              data-testid="chat-input"
              disabled={busy}
            />
            <button type="submit" disabled={busy} className="bg-[#007BFF] hover:bg-[#0056b3] text-white px-3 rounded-sm disabled:opacity-60" data-testid="chat-send-btn">
              <Send className="w-4 h-4" strokeWidth={2} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
